/**
 * Hierarchical TanStack Query keys for Policies: the admin screens, the employee
 * portal and the signed e-mail link (HumanFirewall: an organisation's own policies,
 * read and acknowledged by its own staff).
 *
 * The ESA / Knowledge / Surveys pattern (lib/surveys-module/query-keys.ts): NO `mode`
 * and NO `workspaceId` in any key. Tenant isolation is WorkspaceCacheGuard
 * (queryClient.clear() on a workspace or company switch) plus the `x-workspace-id`
 * header the API client attaches. `useApiModeStore` no longer exists in this tree, so
 * the hfv3-api-mode-conformance skill's "mode first" rule has nothing to read; a key
 * carries the resource and its request only (`qs` is the whole request query string).
 *
 * Three realms, three roots, so an admin's "View as" session and a learner never share
 * a cache entry:
 *   policyKeys         ['policies', …]         the admin session (+ x-workspace-id)
 *   portalPolicyKeys   ['portal-policies', …]  the employee's portal JWT
 *   signedPolicyKeys   ['signed-policies', …]  the e-mailed link (the signature is the credential)
 *
 * Invalidation map (what each write refreshes; hooks.ts and portal-hooks.ts apply it):
 *   create / clone / delete a policy or a draft      → library.all(), approvalsCount
 *   edit a draft (autosave)                          → versions.detail set from the answer; library.lists(), library.detail(id)
 *                                                       and versions.list(id) marked stale (refetchType 'none'); ai.all(id)
 *                                                       invalidated when an item carried an aiSuggestionId (it was accepted)
 *   upload / restore a file                          → versions.detail set; versions.pages(), versions.page, versions.issues;
 *                                                       library.lists() marked stale (refetchType 'none')
 *   submit / withdraw / request changes              → library.all(), the version (isPolicyKey), approvalsCount, dashboard
 *   approve                                          → everything about the policy, library.all(), dashboard, approvalsCount, options
 *   archive / new version / discard a draft          → everything about the policy, library.all(), dashboard, options
 *   assign / ask again / due date / extend / remind  → everything about the policy, library.lists(), dashboard
 *   reset attempts / remove / restore a person       → everything about the policy, library.lists(), dashboard
 *   reminders switches                               → reminders, assignments.all()
 *   AI run / dismiss                                 → ai.all(policy), and the shared AI allowance
 *   portal: page read / tick                         → portalPolicyKeys.task edited in place; list() marked stale (refetchType 'none')
 *   portal: attempt / acknowledge                    → portalPolicyKeys.task, list() (+ receipt on acknowledge)
 *
 * FROZEN after the contract phase: ask the lead for a new key.
 */
import type { PolicyLibrarySection } from './types'

export const policyKeys = {
  all: () => ['policies'] as const,

  // ── Library, the approval inbox and the sidebar badge ──
  library: {
    all: () => [...policyKeys.all(), 'library'] as const,
    lists: () => [...policyKeys.library.all(), 'list'] as const,
    /** `qs` = the whole request query string of that tab. */
    list: (section: PolicyLibrarySection, qs: string) => [...policyKeys.library.lists(), section, qs] as const,
    detail: (policyId: string) => [...policyKeys.library.all(), 'detail', policyId] as const,
    archivePreview: (policyId: string) => [...policyKeys.library.all(), 'archive-preview', policyId] as const,
    inbox: () => [...policyKeys.library.all(), 'inbox'] as const,
    approvalsCount: () => [...policyKeys.library.all(), 'approvals-count'] as const,
  },

  // ── Versions: detail, review, compare, pages, issues ──
  versions: {
    all: (policyId: string) => [...policyKeys.all(), 'versions', policyId] as const,
    list: (policyId: string) => [...policyKeys.versions.all(policyId), 'list'] as const,
    detail: (policyId: string, versionId: string) => [...policyKeys.versions.all(policyId), 'detail', versionId] as const,
    review: (policyId: string, versionId: string) => [...policyKeys.versions.all(policyId), 'review', versionId] as const,
    /** The diff of `versionId` against `withVersionId` (what the newer one changed). Order matters. */
    compare: (policyId: string, versionId: string, withVersionId: string) =>
      [...policyKeys.versions.all(policyId), 'compare', versionId, withVersionId] as const,
    pages: (policyId: string, versionId: string) => [...policyKeys.versions.all(policyId), 'pages', versionId] as const,
    page: (policyId: string, versionId: string, pageNo: number) =>
      [...policyKeys.versions.all(policyId), 'page', versionId, pageNo] as const,
    issues: (policyId: string, versionId: string) => [...policyKeys.versions.all(policyId), 'issues', versionId] as const,
  },

  // ── The Recipients tab and one person's evidence ──
  people: {
    all: (policyId: string) => [...policyKeys.all(), 'people', policyId] as const,
    list: (policyId: string, qs: string) => [...policyKeys.people.all(policyId), 'list', qs] as const,
    person: (policyId: string, attestationId: string) => [...policyKeys.people.all(policyId), 'person', attestationId] as const,
  },

  // ── One policy's figures. `round` null = the current round. ──
  progress: (policyId: string, round: number | null) => [...policyKeys.all(), 'progress', policyId, round ?? 'current'] as const,
  reports: (policyId: string, round: number | null) => [...policyKeys.all(), 'reports', policyId, round ?? 'current'] as const,
  activity: {
    all: (policyId: string) => [...policyKeys.all(), 'activity', policyId] as const,
    series: (policyId: string, qs: string) => [...policyKeys.activity.all(policyId), 'series', qs] as const,
    log: (policyId: string, qs: string) => [...policyKeys.activity.all(policyId), 'log', qs] as const,
  },

  // ── Assignments, reminders, late joiners ──
  assignments: {
    all: (policyId: string) => [...policyKeys.all(), 'assignments', policyId] as const,
    list: (policyId: string, round: number | null) => [...policyKeys.assignments.all(policyId), 'list', round ?? 'current'] as const,
    /** `body` = the stable JSON of the preview body (the caller debounces). */
    preview: (policyId: string, body: string) => [...policyKeys.assignments.all(policyId), 'preview', body] as const,
  },
  reminders: (policyId: string) => [...policyKeys.all(), 'reminders', policyId] as const,
  lateJoiners: (policyId: string) => [...policyKeys.all(), 'late-joiners', policyId] as const,

  // ── Organisation-wide ──
  dashboards: () => [...policyKeys.all(), 'dashboard'] as const,
  /** `department` null = the whole organisation. */
  dashboard: (department: string | null) => [...policyKeys.dashboards(), department ?? 'all'] as const,
  roles: () => [...policyKeys.all(), 'roles'] as const,
  approvers: (excludeSelf: boolean) => [...policyKeys.all(), 'approvers', excludeSelf] as const,

  // ── Picker options (the phishing "require a policy" step, the workflow action) ──
  options: {
    all: () => [...policyKeys.all(), 'options'] as const,
    remedial: () => [...policyKeys.options.all(), 'remedial'] as const,
    /** Which Policies emails are switched on (the dialogs that promise mail). */
    emails: () => [...policyKeys.options.all(), 'emails'] as const,
  },

  // ── AI suggestions stored on a draft ──
  ai: {
    all: (policyId: string) => [...policyKeys.all(), 'ai', policyId] as const,
    suggestions: (policyId: string, versionId: string, qs: string) =>
      [...policyKeys.ai.all(policyId), 'suggestions', versionId, qs] as const,
  },

  // ── v3 (C0, 2026-10-16) ──
  /** GET /settings (P01; W2's Settings page, the Assign page defaults, the workflow page facts). */
  settings: () => [...policyKeys.all(), 'settings'] as const,
  /** GET …/ask-again/preview (E2): `body` = the stable JSON of { who, dueDays }. */
  askAgainPreview: (policyId: string, body: string) => [...policyKeys.assignments.all(policyId), 'ask-again-preview', body] as const,
  /** GET …/rounds/:round/compliance-preview (D3). */
  compliancePreview: (policyId: string, round: number) => [...policyKeys.assignments.all(policyId), 'compliance-preview', round] as const,
  /** GET …/reviews (L5: Review History). */
  reviews: (policyId: string) => [...policyKeys.all(), 'reviews', policyId] as const,
  /** GET …/as-of?day=… (F4): `qs` = the whole request query string. */
  asOf: (policyId: string, qs: string) => [...policyKeys.all(), 'as-of', policyId, qs] as const,
  /** GET /evidence/integrity (L9). */
  integrity: () => [...policyKeys.all(), 'integrity'] as const,
  exemptions: {
    all: () => [...policyKeys.all(), 'exemptions'] as const,
    /** `qs` = the whole request query string (policyId, status, scope, recipientId, page …). */
    list: (qs: string) => [...policyKeys.exemptions.all(), 'list', qs] as const,
  },
  languages: {
    all: (policyId: string) => [...policyKeys.all(), 'languages', policyId] as const,
    /** GET /library/:id/languages (the Languages card). */
    overview: (policyId: string) => [...policyKeys.languages.all(policyId), 'overview'] as const,
    pack: (policyId: string, packId: string) => [...policyKeys.languages.all(policyId), 'pack', packId] as const,
    pages: (policyId: string, packId: string) => [...policyKeys.languages.all(policyId), 'pages', packId] as const,
    page: (policyId: string, packId: string, pageNo: number) => [...policyKeys.languages.all(policyId), 'page', packId, pageNo] as const,
  },
  practice: {
    all: (policyId: string) => [...policyKeys.all(), 'practice', policyId] as const,
    /** GET …/versions/:versionId/preview: `qs` = locale and language. */
    preview: (policyId: string, versionId: string, qs: string) => [...policyKeys.practice.all(policyId), 'preview', versionId, qs] as const,
    tests: (policyId: string, versionId: string) => [...policyKeys.practice.all(policyId), 'tests', versionId] as const,
  },
  auditors: {
    all: () => [...policyKeys.all(), 'auditors'] as const,
    list: () => [...policyKeys.auditors.all(), 'list'] as const,
  },
  auditReports: {
    all: () => [...policyKeys.all(), 'audit-reports'] as const,
    list: () => [...policyKeys.auditReports.all(), 'list'] as const,
    detail: (reportId: string) => [...policyKeys.auditReports.all(), 'detail', reportId] as const,
  },
} as const

/**
 * Is this cached query about this policy? Every admin key that names a policy carries its
 * id as a whole key part, so a write refreshes everything about the policy in one call
 * (`queryClient.invalidateQueries({ predicate: (q) => isPolicyKey(q.queryKey, id) })`)
 * and nothing about another one. Lists, the inbox, the dashboard and the roles carry no
 * policy id and are refreshed by their own keys.
 */
export function isPolicyKey(queryKey: readonly unknown[], policyId: string): boolean {
  return queryKey[0] === policyKeys.all()[0] && queryKey.slice(1).some((part) => part === policyId)
}

/** The employee portal's own root (the portal JWT). */
export const portalPolicyKeys = {
  all: () => ['portal-policies'] as const,
  list: () => [...portalPolicyKeys.all(), 'list'] as const,
  tasks: () => [...portalPolicyKeys.all(), 'task'] as const,
  task: (attestationId: string) => [...portalPolicyKeys.tasks(), attestationId] as const,
  attempt: (attestationId: string, attemptId: string) => [...portalPolicyKeys.all(), 'attempt', attestationId, attemptId] as const,
  /** `evidenceId` = PortalPolicyReceipt.evidenceId (the API's :acknowledgmentId). */
  receipt: (evidenceId: string) => [...portalPolicyKeys.all(), 'receipt', evidenceId] as const,
} as const

/** The signed public link's root (no sign-in; the signature is the credential). */
export const signedPolicyKeys = {
  all: () => ['signed-policies'] as const,
  tasks: () => [...signedPolicyKeys.all(), 'task'] as const,
  task: (attestationId: string, sig: string) => [...signedPolicyKeys.tasks(), attestationId, sig] as const,
  attempt: (attestationId: string, sig: string, attemptId: string) =>
    [...signedPolicyKeys.all(), 'attempt', attestationId, sig, attemptId] as const,
  receipt: (attestationId: string, sig: string) => [...signedPolicyKeys.all(), 'receipt', attestationId, sig] as const,
} as const

/** v3 (C2): a "send a test to myself" link's root (no sign-in; the test run's signature is the credential). */
export const testPolicyKeys = {
  all: () => ['policy-tests'] as const,
  task: (testId: string, sig: string) => [...testPolicyKeys.all(), 'task', testId, sig] as const,
} as const

/**
 * v3 (G1-G6): the external auditor's own root (X-Policy-Audit-Token; never the admin session). Cleared on sign-out.
 * Every key is about the auditor's ONE grant, so no grant id is needed in it.
 */
export const auditorPolicyKeys = {
  all: () => ['policy-audit'] as const,
  me: () => [...auditorPolicyKeys.all(), 'me'] as const,
  overview: () => [...auditorPolicyKeys.all(), 'overview'] as const,
  policies: () => [...auditorPolicyKeys.all(), 'policies'] as const,
  policy: (policyId: string) => [...auditorPolicyKeys.all(), 'policy', policyId] as const,
  people: (policyId: string, qs: string) => [...auditorPolicyKeys.all(), 'people', policyId, qs] as const,
  person: (policyId: string, attestationId: string) => [...auditorPolicyKeys.all(), 'person', policyId, attestationId] as const,
  asOf: (policyId: string, qs: string) => [...auditorPolicyKeys.all(), 'as-of', policyId, qs] as const,
  reports: () => [...auditorPolicyKeys.all(), 'reports'] as const,
  report: (reportId: string) => [...auditorPolicyKeys.reports(), reportId] as const,
} as const
